import { NavLink, useLocation } from 'react-router-dom';
import type { TFunction } from 'i18next';
import './DynamicMenu.scss';
import { Span } from '@uni-space/uni-components';
import classNames from 'classnames';
import { AppChildrenNavItem, AppNavItem, Credential, RouteAccess } from '@shared/types/api';
import { useUserCredentials } from '@shared/utils/auth';
import { useTranslation } from 'react-i18next';

interface DynamicMenuProps {
  navItems: AppNavItem[];
  t: TFunction;
}

export function findNavItemByPath(items: AppNavItem[], targetPath: string): AppNavItem | undefined {
  for (const item of items) {
    if (item.path === targetPath) return item;
    if (item.children) {
      const found = findNavItemByPath(item.children, targetPath);
      if (found) return found;
    }
  }
  return undefined;
}

// цепочка от корня до targetPath (нужна, чтобы понять родительский Credential)
function findChainToPath(items: AppNavItem[], targetPath: string, chain: AppNavItem[] = []): AppNavItem[] | null {
  for (const item of items) {
    const next = [...chain, item];
    if (item.path === targetPath) return next;
    if (item.children) {
      const found = findChainToPath(item.children, targetPath, next);
      if (found) return found;
    }
  }
  return null;
}

// родители: видны, если roles включает item.access (или access отсутствует)
function filterParentsForMenu(items: AppNavItem[], roles: Credential[]): AppNavItem[] {
  return items.filter((it) => it.isParent && (!it.access || roles.includes(it.access)));
}

// дети: видны, если у parentCred есть хотя бы одно требуемое значение доступа
function filterChildrenForMenu(
  children: AppChildrenNavItem[] | undefined,
  parentCred: Credential | undefined,
  hasRoleAccessAny: (role: Credential, need: RouteAccess[]) => boolean,
): AppChildrenNavItem[] | undefined {
  if (!children || children.length === 0) return undefined;

  // если у родителя нет access — считаем раздел открытым, детей не режем
  if (!parentCred) return children;

  const filtered = children.filter((child) => {
    const need = child.accessByCredentialValue ?? [];
    return need.length === 0 || hasRoleAccessAny(parentCred, need);
  });

  return filtered.length ? filtered : undefined;
}

export default function DynamicMenu(props: DynamicMenuProps) {
  const { navItems } = props;
  const location = useLocation();
  const { t } = useTranslation();
  const { pathname } = location;

  const { roles, hasRoleAccessAny } = useUserCredentials();

  const currentItem = findNavItemByPath(navItems, pathname);
  const chain = findChainToPath(navItems, pathname) ?? [];
  const topParent = chain.find((n) => n.isParent);
  const parentCred: Credential | undefined = topParent?.access as Credential | undefined;

  // что показывать:
  // - если у текущего узла есть дети — показываем их, но фильтруем по parentCred
  // - иначе показываем корневых родителей, отфильтрованных по ролям
  const itemsToRender: AppNavItem[] =
    currentItem && currentItem.children?.length
      ? ((filterChildrenForMenu(
          currentItem.children,
          currentItem.isParent ? (currentItem.access as Credential | undefined) : parentCred,
          hasRoleAccessAny,
        ) as AppNavItem[] | undefined) ?? [])
      : filterParentsForMenu(navItems, roles);

  return (
    <>
      <ul className="dynamic-menu__list">
        {itemsToRender.map((item, idx) => {
          const title = t(item.labelKey);

          const isActive = pathname === item.path || (pathname.startsWith(item.path + '/') && item.path !== '/');

          return (
            <li key={`${item.path}_${idx}`} className="dynamic-menu__item">
              <NavLink
                to={item.path}
                className={({ isActive: linkActive }) =>
                  classNames('dynamic-menu__link', {
                    'dynamic-menu__link--active': linkActive || isActive,
                  })
                }
              >
                <Span fs={16}>{title}</Span>
              </NavLink>
            </li>
          );
        })}
      </ul>
      {itemsToRender.length === 0 && <Span>У вас нет доступов</Span>}
    </>
  );
}
